import {
  format,
  differenceInMinutes,
  isSameDay,
  isValid,
  parseISO
} from 'date-fns';

import {
  AbsenceRolloutDutyLine,
  RegisterAbsenceWorkAgreement,
  RegisterAbsenceProposalEntry
} from 'RestAPIs/roster/register-absence';
import { CustomDutyLine } from 'RestAPIs/employees/hours-table/types';
import { getDateFnsLocale } from 'utils/date';

export const DEFAULT_REASON_KEY = 'leave.request.reason';

export const WEEKDAY_OPTIONS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday'
] as const;

export type WorkAgreementSetting = {
  rolloutStartDate: string | null;
  inputDays: string[];
};

export const formatDate = (value?: string) =>
  value
    ? format(parseISO(value), 'dd MMMM yyyy', { locale: getDateFnsLocale() })
    : '-';

export const formatTime = (value?: string) =>
  value ? format(parseISO(value), 'HH:mm') : '-';

export const formatHours = (entry: RegisterAbsenceProposalEntry) => {
  const periods = entry.dutyLine?.periods ?? entry.openRequest?.periods ?? [];
  const totalMinutes = periods.reduce((sum, period) => {
    const minutes = differenceInMinutes(
      parseISO(period.endTime),
      parseISO(period.startTime)
    );
    return sum + Math.max(minutes, 0);
  }, 0);

  if (!totalMinutes) {
    return '0.00u';
  }

  return `${(totalMinutes / 60).toFixed(2)}u`;
};

export const formatPeriods = (entry: RegisterAbsenceProposalEntry) =>
  (entry.dutyLine?.periods ?? entry.openRequest?.periods ?? []).map(
    (period) => ({
      key: `${period.startTime}-${period.endTime}-${period.dutyTypeId}`,
      label: `${formatTime(period.startTime)} - ${formatTime(period.endTime)}`,
      description: period.dutyHourType?.description
    })
  );

export const getDutyCode = (entry: RegisterAbsenceProposalEntry) =>
  entry.dutyLine?.displayDutyCode ??
  entry.dutyLine?.dutyCode ??
  entry.openRequest?.displayDutyCode ??
  entry.openRequest?.dutyCode ??
  '*';

export const getEntryDate = (entry: RegisterAbsenceProposalEntry) =>
  entry.dutyLine?.date ?? entry.openRequest?.date;

export const isSingleDayRange = (start?: string, end?: string) =>
  Boolean(start && end && isSameDay(parseISO(start), parseISO(end)));

export const formatRolloutHours = (value?: number) =>
  value === undefined || value === null ? '-' : `${value.toFixed(2)}u`;

export const getRolloutDutyCode = (entry: AbsenceRolloutDutyLine) =>
  entry.displayDutyCode ?? entry.dutyCode ?? '-';

export const getRolloutWorkAgreementNumber = (entry: AbsenceRolloutDutyLine) =>
  entry.waNr ? String(entry.waNr) : null;

export const getRolloutCostCenter = (entry: AbsenceRolloutDutyLine) =>
  entry.costCenterFullName?.trim() || null;

export const toDateInputValue = (value?: string | null) => {
  if (!value) {
    return null;
  }

  if (!value.includes('T')) {
    return /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null;
  }

  const parsedValue = parseISO(value);
  return isValid(parsedValue) ? format(parsedValue, 'yyyy-MM-dd') : null;
};

/** Ensure a datetime string ends with the Z suffix required by the Java
 *  backend's SimpleDateFormat pattern (yyyy-MM-dd'T'HH:mm:ss.SSS'Z'). */
export const toBackendDateTime = (value: string) =>
  value.endsWith('Z') ? value : `${value}Z`;

export const toApiDateTime = (
  value: string | null,
  referenceDateTime?: string | null
) => {
  if (!value) {
    return null;
  }

  if (value.includes('T')) {
    return toBackendDateTime(value);
  }

  const timePart = referenceDateTime?.match(/T.*$/)?.[0] ?? 'T00:00:00.000Z';
  return toBackendDateTime(`${value}${timePart}`);
};

// Legacy parity: the default rollout date comes from the work agreements
// response (`fteStartDate`), which is already computed by the backend from
// the absence settings. There is no separate settings endpoint for this value.
export const getDefaultRolloutStartDate = (
  workAgreement: RegisterAbsenceWorkAgreement
) => toDateInputValue(workAgreement.fteStartDate);

export const createInitialWorkAgreementSetting = (
  workAgreement: RegisterAbsenceWorkAgreement
): WorkAgreementSetting => ({
  rolloutStartDate: getDefaultRolloutStartDate(workAgreement),
  inputDays: WEEKDAY_OPTIONS.filter((key) => {
    const value =
      workAgreement[`${key}Hours` as keyof RegisterAbsenceWorkAgreement];

    return typeof value === 'boolean'
      ? value
      : !['saturday', 'sunday'].includes(key);
  }).map((key) => key)
});

export const sortNumericValues = (values: number[]) =>
  [...values].sort((a, b) => a - b);

export const serializeWorkAgreementSettings = (
  ids: number[],
  settings: Record<number, WorkAgreementSetting>
) =>
  sortNumericValues(ids).map((id) => {
    const setting = settings[id];
    return `${id}:${setting?.rolloutStartDate ?? ''}:${(
      setting?.inputDays ?? []
    ).join(',')}`;
  });

export const toEditableDutyLine = (
  entry: RegisterAbsenceProposalEntry,
  index: number
): CustomDutyLine | null => {
  if (!entry.dutyLine) {
    return null;
  }

  return {
    id: `leave-request-duty-${index}`,
    dutyLineId: entry.dutyLine.dutyLineId ?? 0,
    dutyId: entry.dutyLine.id ?? 0,
    brId: entry.dutyLine.costCenterId ?? 0,
    brDynId: 0,
    waId: entry.dutyLine.workAgreementId ?? 0,
    createdBy: 0,
    dutyDate: entry.dutyLine.date,
    dutyDatetimeBegin:
      entry.dutyLine.periods[0]?.startTime ?? entry.dutyLine.date,
    dutyDatetimeEnd:
      entry.dutyLine.periods[entry.dutyLine.periods.length - 1]?.endTime ??
      entry.dutyLine.date,
    publishDate: entry.dutyLine.date,
    visibleUntil: entry.dutyLine.date,
    weekNumber: 0,
    totalHours: 0,
    workAgreementNr: entry.dutyLine.workAgreementId ?? 0,
    statusSvc: '',
    dutyCode: entry.dutyLine.dutyCode ?? '',
    displayDutyCode:
      entry.dutyLine.displayDutyCode ?? entry.dutyLine.dutyCode ?? '*',
    costCenterName: '',
    periods: entry.dutyLine.periods.map((period, periodIndex) => ({
      dutyLinePeriodId: periodIndex,
      startTime: period.startTime,
      endTime: period.endTime,
      hours:
        differenceInMinutes(
          parseISO(period.endTime),
          parseISO(period.startTime)
        ) / 60,
      dutyHourType: {
        id: period.dutyHourType?.id ?? period.dutyTypeId,
        dutyHourTypeId: period.dutyHourType?.id ?? period.dutyTypeId,
        code: period.dutyHourType?.description ?? '',
        description: period.dutyHourType?.description ?? ''
      }
    })),
    absenceIds: [],
    qualificationIds: [],
    notes: [],
    requestId: entry.openRequest?.id ?? entry.openRequest?.requestId ?? null,
    flags: [],
    isAllowedToDelete: true,
    dateKey: entry.dutyLine.date,
    date: entry.dutyLine.date
  };
};

export const workAgreementLabel = (
  workAgreement: RegisterAbsenceWorkAgreement
) =>
  [
    workAgreement.nr,
    workAgreement.functionDescription,
    workAgreement.brName,
    workAgreement.contractKind
  ]
    .filter(Boolean)
    .join(' - ');
